<template>
  <div>
    <b-overlay :show="load">
    <b-card
      header-text-variant="dark"
      :border-variant="groupSettlementCancelled || groupSettlementInvalid ? 'danger' : null"
      :header-border-variant="groupSettlementCancelled || groupSettlementInvalid ? 'danger' : null"
      v-bind:class="{'group-settlement-cancelled-card' : groupSettlementCancelled, 'group-settlement-settled-card' : groupSettlementSettled, 'group-settlement-active-card': groupSettlementActive}"
      class="mb-2"
      v-if="dataFetched">
      <div slot="header">
        <context-header :title="$t('groupSettlement.groupSettlementSingle')" alternativeRoute="group-settlement/group-settlement-information">
          <template slot="badges">
            <span>
              <b-badge variant="danger" v-if="groupSettlementCancelled">{{$t('groupSettlement.badgeCancelled')}}</b-badge>
              <b-badge variant="success" v-if="groupSettlementSettled">{{$t('groupSettlement.badgeSettled')}}</b-badge>
              <b-badge variant="danger" v-if="groupSettlementInvalid">{{$t('groupSettlement.badgeInvalid')}}</b-badge>
            </span>
          </template>
        </context-header>
      </div>
      <b-card v-if="groupSettlementInvalid || groupSettlementFlagged"
          bg-variant="danger"
          text-variant="white"
          class="text-center mb-3">
            <p class="card-text">{{$t('groupSettlement.groupSettlementInvalidBanner')}}
              <br>
              <a class="link-text" :href="activationDocumentationLink">{{$t('groupSettlement.groupSettlementActivateLink')}}</a>
            </p>
      </b-card>  
      <b-row>
        <b-col cols="4">
          <b-form-fieldset class="property-label-caption" :label="$t('groupSettlement.groupSettlementNumber')">
            <label class="property-label">
              {{groupSettlement.groupSettlementNumber}}
            </label>
          </b-form-fieldset>
        </b-col>
        <b-col cols="4" v-if="!!groupSettlement.createdName">
          <b-form-fieldset class="property-label-caption" :label="$t('groupSettlement.groupSettlementCreator')">
            <label class="property-label">
              <router-link :to="{name: 'userInfoPage', params:{userId: groupSettlement.createdId}}">
                {{groupSettlement.createdName ? groupSettlement.createdName: groupSettlement.createdId}}
              </router-link>
            </label>
          </b-form-fieldset>
        </b-col>
        <b-col cols="4" v-if="hasInvoiceReviewAllClaim">
          <b-form-fieldset class="property-label-caption" :label="$t('groupSettlement.columnDebtor')">
            <label class="property-label">
              <router-link :to="{name: 'approvedUserPage', params:{registrationId: groupSettlement.debtorId}}">
                {{groupSettlement.debtorName ? groupSettlement.debtorName : groupSettlement.debtorId}}
              </router-link>
            </label>
          </b-form-fieldset>
        </b-col>

      <b-col v-if="canReportIssue">
          <b-img class="report-image" @click="reportSettlementIssue" v-b-tooltip.hover :title="$t('general.technicalSupport')" right src="~@/assets/images/report-issue.svg"/>
      </b-col>
      </b-row>
      <b-row>
        <b-col cols="4">
          <b-form-fieldset class="property-label-caption" :label="$t('groupSettlement.columnAmount')">
            <label class="property-label">
              {{formatAmount(groupSettlement.amount)}}
            </label>
          </b-form-fieldset>        
        </b-col>
        <b-col cols="4">
          <b-form-fieldset class="property-label-caption" :label="$t('groupSettlement.columnCreationDate')">
            <label class="property-label">{{formatShortDate(groupSettlement.creationDate)}}</label>
          </b-form-fieldset>        
        </b-col>
        <b-col cols="4">
          <b-form-fieldset class="property-label-caption" :label="$t('groupSettlement.columnCreditor')">
            <label class="property-label">
              <router-link :to="{name: 'approvedUserPage', params:{registrationId: groupSettlement.creditorId}}">
                {{groupSettlement.creditorName ? groupSettlement.creditorName : groupSettlement.creditorId}}
              </router-link>
            </label>
          </b-form-fieldset>        
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="4">
          <b-form-fieldset class="property-label-caption" :label="$t('groupSettlement.columnDueDate')">
            <label class="property-label">
              {{formatShortDate(groupSettlement.dueDate)}}
            </label>
          </b-form-fieldset>
        </b-col>
        <b-col cols="4">
          <b-form-fieldset class="property-label-caption" :label="$t('groupSettlement.columnInvoiceCount')">
              <label class="property-label">
              {{groupSettlement.invoiceCount}}
            </label>
          </b-form-fieldset>        
        </b-col>
        <b-col cols="4" v-if="groupSettlement.settlementDate">
          <b-form-fieldset class="property-label-caption" :label="$t('groupSettlement.columnSettlementDate')">
            <label class="property-label">
              {{formatShortDate(groupSettlement.settlementDate)}}
            </label>
          </b-form-fieldset>
        </b-col>
        <b-col cols="4">
          <b-form-fieldset class="property-label-caption" :label="$t('groupSettlement.columnStatus')">
            <label class="property-label">
              {{$t('groupSettlement.groupSettlementStatus.' + groupSettlement.groupSettlementStatus)}}
            </label>
          </b-form-fieldset>
        </b-col>
      </b-row>
      <hr>
      <b-row class="mb-1">
        <b-col col>
          <span class="col-form-legend">
            {{$t('groupSettlement.invoiceTableHeader')}}
          </span>
          <b-button class="ml-2" :title="$t('groupSettlement.groupSettlementAddInvoices')" v-on:click="showAddInvoicesInput" :disabled="!isGroupSettlementEditable || hasInvalidInvoices" v-if="!showMassAdditionInput && isGroupSettlementEditable && !hasInvalidInvoices" variant="outline-secondary">
            <i class="material-icons">playlist_add</i>
          </b-button>
          <b-button class="ml-2" :title="$t('groupSettlement.groupSettlementCancelAddInvoices')" v-on:click="hideAddInvoicesInput" v-if="showMassAdditionInput" variant="outline-secondary">
            <i class="material-icons">cancel</i>
          </b-button>
        </b-col>
        <b-col cols="md-auto">
          <b-button v-on:click="toggleShowTable" v-if="showTable && isGroupSettlementEditable" :title="$t('groupSettlement.groupSettlementInvoicesShowInput')">
            <i class="material-icons">list</i>
          </b-button>
          <b-button v-on:click="toggleShowTable" v-if="showTable && !isGroupSettlementEditable" :title="$t('groupSettlement.groupSettlementInvoicesShowText')">
            <i class="material-icons">notes</i>
          </b-button>
          <b-button v-on:click="toggleShowTable" v-if="!showTable" :title="$t('groupSettlement.groupSettlementInvoicesShowTable')">
            <i class="material-icons">table_chart</i>
          </b-button>
        </b-col>
      </b-row>
      <b-row v-if="showMassAdditionInput">
        <b-col>
          <b-form-fieldset>
            <b-form-textarea
              :rows="15"
              :max-rows="32"
              v-model="massAdditionInput"
              :placeholder="$t('groupSettlement.massAdditionalInput')"
              class="monospace-input"/>
          </b-form-fieldset>
        </b-col>
      </b-row>
      <b-row v-if="showMassAdditionInput">
        <b-col>
          <b-button class="mb-2" v-on:click="groupSettlementAddInvoice" variant="primary">
            {{ $t('groupSettlement.groupSettlementAddInvoice') }}
            <loader :show="load" type="button"></loader>
          </b-button>
        </b-col>
      </b-row>
      <b-row v-if="showAdditionalErrorContainer && showMassAdditionInput">
        <b-col>
          <b-card no-body header-bg-variant="danger"
            :header="$t('groupSettlement.massInputError.tableHeader')"
            header-text-variant="white"
            class="m-1">
            <div class="list-container">
              <b-list-group>
                <b-list-group-item :key="error" v-for="error in additionalErrorList">{{error}}</b-list-group-item>
              </b-list-group>
            </div>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col v-if="showTable">
          <b-row>
            <b-col class="mt-2">
              <b-table hover responsive
              :items="tableItems"
              :fields="invoiceTableFields">
                <template slot="settlementAmount" slot-scope="data">
                  {{formatAmount(data.item.settlementAmount)}}
                </template>
                <template v-slot:cell(status)="data">
                  <b-link :to="{ name: 'singleInvoiceRoute', params: {invoiceId: data.item.invoiceId}}" class="invisible-link" target='_blank'>
                    <button v-if="data.item.currentInvoiceStatus === invoiceStatus.SETTLED" class="btn btn-sm btn-success">{{$t('invoices.invoiceSettled')}}</button>
                    <button v-else-if="data.item.currentInvoiceStatus === invoiceStatus.ACTIVE || data.item.currentInvoiceStatus === invoiceStatus.PROINVOICE" class="btn btn-sm btn-info">{{$t('invoices.invoiceStatus.' + data.item.currentInvoiceStatus)}}</button>
                    <button v-else-if="data.item.currentInvoiceStatus === invoiceStatus.PARTIALLY_SETTLED" class="btn btn-sm btn-info">{{$t('invoices.invoiceStatus.' + data.item.currentInvoiceStatus)}}</button>
                    <button v-else-if="data.item.currentInvoiceStatus === invoiceStatus.ASSIGNED" class="btn btn-sm btn-warning">{{$t('invoices.invoiceStatus.' + data.item.currentInvoiceStatus)}}</button>
                    <button v-else class="btn btn-sm" v-bind:class="{ 'btn-danger': data.item.currentInvoiceStatus === invoiceStatus.CANCELLED }">{{$t('invoices.invoiceStatus.' + data.item.currentInvoiceStatus)}}</button>
                  </b-link>
                </template>
                <template v-slot:cell(invoiceId)="data">
                  <b-link :to="{ name: 'singleInvoiceRoute', params: {invoiceId: data.item.invoiceId}}" class="invisible-link" target='_blank'>
                    {{data.item.invoiceId}}
                  </b-link>
                </template>
                <template v-slot:cell(settlementAmount)="data">
                  <b-link :to="{ name: 'singleInvoiceRoute', params: {invoiceId: data.item.invoiceId}}" class="invisible-link" target='_blank'>
                    {{formatAmount(data.item.settlementAmount)}}
                  </b-link>
                </template>
                <template v-slot:cell(selected)="data">
                  <b-form-checkbox @click.native.stop :id="'checkbox'+data.index" v-model="data.item.selected">
                  </b-form-checkbox>
                </template>
                <template v-slot:cell(errorCode)="data">
                  <b-link :to="{ name: 'singleInvoiceRoute', params: {invoiceId: data.item.invoiceId}}" class="invisible-link" target='_blank' v-if="data.item.errorCode !== null">
                    <!-- <span v-if="(data.item.errorCode !== null)" :title="$t('apiStatusCodes.' + data.item.errorCode)">{{data.item.errorCode}}</span> -->
                    <button class="btn btn-sm btn-danger" :title="$t('apiStatusCodes.' + data.item.errorCode)">{{ $t('groupSettlement.invoiceError') }}</button>
                  </b-link>
                </template>
              </b-table>
            </b-col>
          </b-row>
          <b-row v-if="(isGroupSettlementEditable && hasInvalidInvoices) || isGroupSettlementEditable">
            <b-col>
              <!-- This div is a hack to trick vue's reactivity mechanism. I can't link to problem that explains it -->
              <!-- tldr of the problem is that when this row is hidden and the one below is not  -->
              <!-- the button that selects invalid invoices and the button for saving changes -->
              <!-- have the same selectPath and Vue calls both actions when you click on them -->
              <!-- the extra div below changes that path and forces Vue to distinguish those two buttons -->
              <div>
                <b-button class="mr-2" :title="$t('groupSettlement.selectInvalidInvoices')" v-on:click="selectInvalidInvoices" v-if="isGroupSettlementEditable && hasInvalidInvoices" variant="secondary">
                  <i class="material-icons">ballot</i>
                </b-button>
                <b-button v-if="isGroupSettlementEditable" :title="$t('groupSettlement.groupSettlementRemoveInvoice')" v-on:click="groupSettlementRemoveInvoice" variant="danger" :disabled="!canRemoveInvocie">
                  <i class="material-icons">delete_sweep</i>
                  <loader :show="load" type="button"></loader>
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-col>
        <b-col v-if="!showTable && !isGroupSettlementEditable">
          <ul class="list-group">
            <li
              v-bind:class="{
                  'group-settlement-settled-card-list' : groupSettlementSettled,
                  'list-group-item-danger': [invoiceStatus.CANCELLED, invoiceStatus.INVALID].includes(item.status),
                  'list-group-item-success': [invoiceStatus.SETTLED].includes(item.status)
                }"
              v-for="item in groupSettlement.groupSettlementItems"
              :key="item.id"
              class="list-group-item">
              {{item.invoiceId}}&nbsp;{{item.settlementAmount}}
            </li>
          </ul>
        </b-col>
        <b-col v-if="!showTable && isGroupSettlementEditable">
          <b-row>
            <b-col>
              <b-form-fieldset>
                <b-form-textarea
                  :rows="15"
                  :max-rows="32"
                  v-model="massInput"
                  class="monospace-input"/>
              </b-form-fieldset>
            </b-col>
          </b-row>
          <b-row v-if="showErrorContainer">
            <b-col>
              <b-card no-body header-bg-variant="danger"
                :header="$t('groupSettlement.massInputError.tableHeader')"
                header-text-variant="white"
                class="m-1">
                <div class="list-container">
                  <b-list-group>
                    <b-list-group-item :key="error" v-for="error in errorList">{{error}}</b-list-group-item>
                  </b-list-group>
                </div>
              </b-card>
            </b-col>
          </b-row>
          <hr>
          <b-row>
            <b-col>
              <b-button v-if="isGroupSettlementEditable"  v-b-modal.editSingleConfirmationModal variant="primary" :disabled="load">
                {{ $t('groupSettlement.groupSettlementEditSave') }}
                <loader :show="load" type="button"></loader>
              </b-button>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <div slot="footer" class="text-right" v-if="showFooter">
        <b-button v-if="showActivateButton" v-b-modal.groupSettlementActivateConfirmationModal :variant="showActivateButton ? 'primary' : 'secondary'" :disabled="load || hasInvalidInvoices" :title="!hasInvalidInvoices ? $t('groupSettlement.groupSettlementActivateTitle') : $t('groupSettlement.groupSettlementActivateTitleDisabled')">
          {{ $t('groupSettlement.groupSettlementActivate') }}
          <loader :show="load" type="button"></loader>
        </b-button>
        <b-button v-if="showDeactivateButton" v-on:click="groupSettlementDeactivate" variant="primary" :disabled="load" :title="$t('groupSettlement.groupSettlementDeactivateTitle')">
          {{ $t('groupSettlement.groupSettlementDeactivate') }}
          <loader :show="load" type="button"></loader>
        </b-button>
        <b-button 
          v-if="showCancelButton"
          v-b-modal.groupSettlementCancelConfirmationModal
          variant="success"
          :disabled="load"
          :title="$t('groupSettlement.groupSettlementCancelTitle')" >
          {{ $t('groupSettlement.groupSettlementCancel') }}
          <loader :show="load" type="button"></loader>
        </b-button>
      </div>
      <div>
        <b-modal id="groupSettlementActivateConfirmationModal" @ok="groupSettlementActivate" :ok-title="$t('forms.submit')" :cancel-title="$t('forms.cancel')">
            {{ $t('groupSettlement.groupSettlementActivationConfirmation') }}
        </b-modal>
        <b-modal id="groupSettlementCancelConfirmationModal" @ok="groupSettlementCancel" :ok-title="$t('forms.submit')" :cancel-title="$t('forms.cancel')">
            {{ $t('groupSettlement.groupSettlementCancelationConfirmation') }}
        </b-modal>
        <b-modal id="editSingleConfirmationModal" @ok="validateBeforeSubmit" :ok-title="$t('forms.submit')" :cancel-title="$t('forms.cancel')">
          {{ $t('groupSettlement.groupSettlementEditConfirmation') }}
        </b-modal>
      </div>
    </b-card>
    <b-card v-else-if="hasErrorWhenFetchingData">
      <b-row>
        <b-col>
          <p>
            {{ $t('groupSettlement.groupSettlementFetchingDataFailed') }}
          </p>
        </b-col>
      </b-row>
    </b-card>
    <b-card v-else>
      <b-row>
        <b-col>
          <p>
            {{ $t('groupSettlement.groupSettlementFetchingData') }}
          </p>
        </b-col>
      </b-row>
    </b-card>
    <b-modal ref="reportIssueModal"
        :title="currentIssue === this.IssueCategory[0] ? $t('issues.reportIssueLiability') : $t('issues.reportIssueSettlement')"
        header-bg-variant="warning"
        :ok-title="$t('forms.send')"
        size="lg"
        @ok="submitIssueReport"
        ok-only>
        <b-container fluid>
            <b-row>
                <b-col col md="12">
                    <b-form-fieldset :label="$t('issues.reportId')">
                        <b-form-input v-model="currentIssueReport.resourceId" disabled/>
                    </b-form-fieldset>
                </b-col>
            </b-row>
            <b-row>
                <b-col col md="12">
                    <b-form-fieldset :label="$t('issues.reportCategories')">
                        <b-form-select @input="categoryChanged" v-model="selectedCategory" :options="formattedIssueCategories"/>
                    </b-form-fieldset>
                </b-col>
            </b-row>
            <div v-if="currentIssueCategory && currentIssueCategory.requiresData">
                <div v-if="currentIssue.ID === IssueCategory[1].ID && currentIssueCategory.id === 3">
                    <b-row>
                        <b-col col md="12">
                            <b-form-fieldset :label="$t('issues.reportSettlementAmount')">
                                <b-input-group :right="$t('invoices.invoiceCurrency')">
                                        <money name="issues.amount"
                                                v-bind="currency"
                                                v-model="currentIssueAmount"
                                                class="form-control"/>
                                </b-input-group>
                            </b-form-fieldset>
                        </b-col>
                    </b-row>
                </div>
                <div v-if="currentIssue.ID === IssueCategory[1].ID && currentIssueCategory.id === 4 && hasInvoiceSettlements">
                    <b-row>
                        <b-col col md="12">
                            <b-form-fieldset :label="$t('issues.reportSettlementId')">
                                <b-form-select :options="invoiceSettlementIDs" v-model="currentIssueSettlementId" />
                            </b-form-fieldset>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col col md="12">
                            <b-form-fieldset :label="$t('issues.reportSettlementAmount')">
                                <b-input-group :right="$t('invoices.invoiceCurrency')">
                                    <money name="issues.amount" v-bind="currency" v-model="currentIssueAmount" class="form-control" />
                                </b-input-group>
                            </b-form-fieldset>
                        </b-col>
                    </b-row>
                </div>
                <b-row v-if="currentIssue.ID === IssueCategory[0].ID && currentIssueCategory.id === 1">
                    <b-col col md="12">
                        <b-form-fieldset :label="$t('issues.reportData')">
                            <b-form-select :options="invoiceFields" v-model="currentIssueReport.data" :state="false"/>
                        </b-form-fieldset>
                    </b-col>
                </b-row>
                <b-row v-else>
                    <b-col col md="12">
                        <b-form-fieldset :label="$t('issues.reportData')">
                            <b-form-input v-model="currentIssueReport.data" :state="false"/>
                            <small class="field-description">{{currentIssueCategory.dataDescriptionCyr}}</small>
                        </b-form-fieldset>
                    </b-col>
                </b-row>
            </div>

            <b-row>
                <b-col col md="12">
                    <b-form-fieldset :label="$t('issues.reportUserComment')">
                        <textarea rows="4" type="text" class="form-control" v-model="currentIssueReport.userComment"/>
                    </b-form-fieldset>
                </b-col>
            </b-row>

            <div v-if="currentIssueCategory && currentIssueCategory.categoryHelp">
                <b-row>
                    <b-col col md="12">
                        <b-card bg-variant="danger" text-variant="white">
                            <p class="card-text">{{currentIssueCategory.categoryDescriptionCyr}}</p>
                            <a style="color: #fa9a4b;" :href="helpLink(currentIssueCategory.categoryHelp)" target="_blank">{{$t('issues.reportIssueHelpLink')}}</a>
                        </b-card>
                    </b-col>
                </b-row>
            </div>
       </b-container>
    </b-modal>    
    </b-overlay>
  </div>
</template>
<script>
  import Vue from 'vue'
  import { EventBus } from '@/router/bus'
  import ContextHeader from '@/components/misc/context-header/ContextHeader'
  import i18n from '@/i18n'
  import Loader from '@/components/misc/Loader'
  import Vuelidate from 'vuelidate'
  import { validationMixin } from '@/mixins/validation'
  import { MassInputValidator } from '../validators'
  import { required } from 'vuelidate/lib/validators'
  import { massInputValidationMixin } from '../validators/mixin'
  import { hasInvoiceReviewAllClaim, getOrganizationId } from '@/router/auth'
  import { Money } from 'v-money'

  Vue.use(Vuelidate)

  export default {
    mixins: [validationMixin, massInputValidationMixin],
    components: {
      Loader,
      ContextHeader,
      Money
    },
    created () {
      this.fetchData()
      this.organizationId = parseInt(getOrganizationId())
    },
    data () {
      return {
        load: false,
        hasInvoiceReviewAllClaim: hasInvoiceReviewAllClaim(),
        currency: {
          decimal: this.$store.getters.decimalFormat,
          thousands: this.$store.getters.decimalFormat === ',' ? '.' : ',',
          prefix: '',
          suffix: '',
          precision: 2,
          masked: false
        },
        IssueCategory: [
          { DESCRIPTION: 'InvoiceLiability', ID: 1 },
          { DESCRIPTION: 'InvoiceSettlement', ID: 2 }
        ],
        groupSettlementStatus: {
          PENDING: 1,
          ACTIVE: 2,
          SETTLED: 3,
          CANCELLED: 4,
          INVALID: 5,
          FLAGGED: 6
        },
        invoiceStatus: {
          ACTIVE: 1, // ignore when looking at line errors
          INVALID: 2,
          CANCELLED: 3,
          PARTIALLY_SETTLED: 4, // ignore when looking at line errors
          SETTLED: 5,
          ASSIGNED: 6,
          PROINVOICE: 7
        },
        errorList: [],
        additionalErrorList: [],
        invoiceErrors: [],
        tableItems: [],
        showTable: true,
        groupSettlement: {
          groupSettlementNumber: '',
          groupSettlementItems: []
        },
        organizationId: 0,
        currentIssueReport: {},
        currentIssue: null,
        currentIssueCategories: null,
        currentIssueCategory: null,
        selectedCategory: null,
        formattedIssueCategories: null,
        currentIssueAmount: 0,
        currentIssueSettlementId: null,
        NotificationTypes: {
          IssueProcessed: 5
        },
        massInput: '',
        massAdditionInput: '',
        showMassAdditionInput: false,
        dataFetched: false,
        hasErrorWhenFetchingData: false,
        groupSettlementGetLink: Vue.$apiConfig.groupSettlementGet + '/' + this.$route.params.groupSettlementId,
        groupSettlementActivateLink: Vue.$apiConfig.activateGroupSettlementPost,
        groupSettlementDeactivateLink: Vue.$apiConfig.deactivateGroupSettlementPost,
        groupSettlementCancelLink: Vue.$apiConfig.cancelGroupSettlementPost,
        editGroupSettlementLink: Vue.$apiConfig.updateGroupSettlementPost,
        activationDocumentationLink: Vue.$config.help_root + '/' + 'group-settlement/group-settlement-information/#aktivacija'
      }
    },
    computed: {
      invoiceTableFields () {
        let retVal = [
          { key: 'invoiceId', label: i18n.t('invoices.columnInvoiceNumber'), sortable: true, sortDirection: 'desc' },
          { key: 'settlementAmount', label: i18n.t('invoices.columnSettlementAmount'), sortable: true },
          { key: 'status', label: i18n.t('invoices.columnInvoiceStatus'), sortable: true }
        ]
        if (this.invoiceErrors.length > 0 && ![this.groupSettlementStatus.SETTLED, this.groupSettlementStatus.CANCELLED].includes(this.groupSettlement.groupSettlementStatus)) {
          retVal.unshift({key: 'errorCode', label: i18n.t('groupSettlement.columnInvoiceErrorCode'), sortble: true, class: 'small-column'})
        }
        if (this.isGroupSettlementEditable) {
          retVal.unshift({key: 'selected', label: ' ', sortable: false, class: 'tiny-column'})
        }
        return retVal
      },
      invalidInvoiceStatuses () {
        return [this.invoiceStatus.CANCELLED, this.invoiceStatus.INVALID, this.invoiceStatus.SETTLED]
      },
      isDevelopment () {
        return !this.isProductionEnvironment()
      },
      selectedInvoices () {
        let retVal = this.tableItems.filter(item => {
          return item.selected === true
        })
        return retVal
      },
      hasInvalidInvoices () {
        let conditionOne = this.groupSettlement.groupSettlementItems.filter(item => this.invalidInvoiceStatuses.includes(item.status)).length > 0
        let conditionTwo = this.tableItems.filter(item => item.errorCode !== null).length > 0
        return conditionOne || conditionTwo
      },
      showActivateButton () {
        return this.groupSettlement.groupSettlementStatus === this.groupSettlementStatus.PENDING
      },
      showDeactivateButton () {
        return [this.groupSettlementStatus.ACTIVE, this.groupSettlementStatus.INVALID, this.groupSettlementStatus.FLAGGED].includes(this.groupSettlement.groupSettlementStatus)
      },
      showCancelButton () {
        return [this.groupSettlementStatus.PENDING, this.groupSettlementStatus.ACTIVE, this.groupSettlementStatus.INVALID, this.groupSettlementStatus.FLAGGED].includes(this.groupSettlement.groupSettlementStatus)
      },
      isGroupSettlementEditable () {
        return [this.groupSettlementStatus.PENDING].includes(this.groupSettlement.groupSettlementStatus)
      },
      canRemoveInvocie () {
        return this.selectedInvoices.length > 0
      },
      showFooter () {
        return this.showActivateButton || this.showCancelButton || this.isGroupSettlementEditable || this.showDeactivateButton
      },
      groupSettlementCancelled () {
        return this.groupSettlement.groupSettlementStatus === this.groupSettlementStatus.CANCELLED
      },
      groupSettlementActive () {
        return this.groupSettlement.groupSettlementStatus === this.groupSettlementStatus.ACTIVE
      },
      groupSettlementInvalid () {
        return this.groupSettlement.groupSettlementStatus === this.groupSettlementStatus.INVALID
      },
      groupSettlementSettled () {
        return this.groupSettlement.groupSettlementStatus === this.groupSettlementStatus.SETTLED
      },
      groupSettlementFlagged () {
        return this.groupSettlement.groupSettlementStatus === this.groupSettlementStatus.FLAGGED
      },
      showErrorContainer () {
        return this.errorList.length > 0
      },
      showAdditionalErrorContainer () {
        return this.additionalErrorList.length > 0
      },
      isCurrentUserDebtor () {
        return this.groupSettlement.debtorId && (this.groupSettlement.debtorId === this.organizationId)
      },
      canReportIssue () {
        return this.isCurrentUserCreditor || this.isCurrentUserDebtor
      }
    },
    methods: {
      fetchData () {
        this.load = true
        this.$http.get(this.groupSettlementGetLink)
        .then(response => {
          this.load = false
          this.dataFetched = true
          this.setSettlement(response.body.payload)
        }, error => {
          this.load = false
          this.hasErrorWhenFetchingData = true
          this.logConsole(error)
          this.formatErrorResponse(error)
        })
      },
      selectInvalidInvoices () {
        this.tableItems.forEach((item) => {
          if (this.invalidInvoiceStatuses.includes(item.status)) {
            item.selected = true
          }
          if (item.errorCode !== null) {
            item.selected = true
          }
        })
      },
      setSettlement (payload) {
        this.groupSettlement = payload.groupSettlement
        this.setMassInput()
        this.tableItems = JSON.parse(JSON.stringify(this.groupSettlement.groupSettlementItems))
        this.invoiceErrors = []
        if (payload.groupSettlementItems) {
          this.invoiceErrors = payload.groupSettlementItems
        }
        this.tableItems.forEach((item) => {
          Vue.set(item, 'errorCode', null)
          Vue.set(item, 'selected', false)
          let doStyle = ![this.groupSettlementStatus.SETTLED, this.groupSettlementStatus.CANCELLED].includes(this.groupSettlement.groupSettlementStatus)
          if (doStyle) {
            if (this.invalidInvoiceStatuses.includes(item.status)) {
              Vue.set(item, '_rowVariant', 'danger')
            }
            if (this.invoiceStatus.SETTLED === item.status) {
              Vue.set(item, '_rowVariant', 'success')
            }
          }
          let invoiceError = this.invoiceErrors.find((errorEntry) => {
            return item.invoiceId === errorEntry.groupSettlementItem.invoiceId
          })
          if (typeof invoiceError !== 'undefined') {
            if (![this.invoiceStatus.ACTIVE, this.invoiceStatus.PARTIALLY_SETTLED].includes(item.status)) {
              if (doStyle) {
                Vue.set(item, '_rowVariant', 'danger')
              }
              Vue.set(item, 'errorCode', invoiceError.groupSettlementItemError.code)
            }
          }
        })
      },
      setMassInput () {
        this.massInput = this.groupSettlement.groupSettlementItems.map(item => {
          return item.invoiceId + ' ' + item.settlementAmount
        }).join('\n')
      },
      showAddInvoicesInput () {
        this.showMassAdditionInput = true
        this.additionalErrorList = []
      },
      hideAddInvoicesInput () {
        this.showMassAdditionInput = false
        this.massAdditionInput = ''
      },
      groupSettlementStatusChange (endpointLink, successMessage) {
        this.load = true
        this.$http.post(endpointLink, {groupSettlementId: this.groupSettlement.groupSettlementId})
        .then(response => {
          this.load = false
          // The response to the change status call does not return proper line errors,
          // We have to do another call, or just change the status of the settlement
          // this.setSettlement(response.data.payload)
          this.fetchData()
          EventBus.$emit('alert', {
            variant: 'success',
            message: successMessage,
            errors: []
          })
        }, error => {
          this.load = false
          this.logConsole(error)
          this.formatErrorResponse(error)
        })
      },
      groupSettlementActivate () {
        this.groupSettlementStatusChange(this.groupSettlementActivateLink, this.$t('groupSettlement.groupSettlementActivated'))
      },
      groupSettlementDeactivate () {
        this.groupSettlementStatusChange(this.groupSettlementDeactivateLink, this.$t('groupSettlement.groupSettlementDeactivated'))
      },
      groupSettlementRemoveInvoice () {
        this.load = true
        let payload = {}
        let slectedArray = this.tableItems.filter((item) => {
          return item.selected
        })
        slectedArray = slectedArray.map(o => o['id'])
        payload = JSON.parse(JSON.stringify(this.groupSettlement))
        payload.groupSettlementItems = payload.groupSettlementItems.filter((item) => {
          return !slectedArray.includes(item.id)
        })
        this.$http.post(this.editGroupSettlementLink, payload)
        .then(response => {
          this.load = false
          // we don't do this for know because the response is malformed,
          // this.setSettlement(response.data.payload.groupSettlement)
          // instead we just re-fetch the data
          this.fetchData()
        }, error => {
          this.load = false
          this.logConsole(error)
          this.formatErrorResponse(error)
        })
      },
      groupSettlementAddInvoice () {
        this.$v.massAdditionInput.$touch()
        if (this.$v.massAdditionInput.$invalid) {
          EventBus.$emit('alert', {
            variant: 'danger',
            message: this.$t('errors.someErrorsOccuredCheckListBelow'),
            errors: this.getValidationErrors('groupSettlement')
          })
          this.additionalErrorList = this.getMassInputErrors(this.massAdditionInput, {minimumInvoices: 1})
        } else {
          this.additionalErrorList = []
          this.load = true
          let payload = JSON.parse(JSON.stringify(this.groupSettlement))
          this.parseMassInput(this.massAdditionInput).forEach(row => {
            if (row === '') {
              return
            }
            let temp = row.match(/\S+/g)
            let invoiceId = temp[0]
            let amount = temp[1] ? Number(temp[1]) : 0
            payload.groupSettlementItems.push({InvoiceId: invoiceId, SettlementAmount: amount})
          })
          this.$http.post(this.editGroupSettlementLink, payload)
          .then(response => {
            this.load = false
            this.massAdditionInput = ''
            this.showMassAdditionInput = false
            this.fetchData()
          }, error => {
            if (typeof error.body.payload !== undefined && error.body.payload !== null) {
              if (typeof error.body.payload.groupSettlementItems !== 'undefined') {
                let localErrorList = []
                Array.from(error.body.payload.groupSettlementItems).forEach(row => {
                  if (typeof row.groupSettlementItemError !== 'undefined') {
                    let errorPrefix = this.$t('groupSettlement.massInputError.returnedCodePrefix', [row.groupSettlementItem.invoiceId])
                    let errorMsg = this.$t('apiStatusCodes.' + row.groupSettlementItemError.code)
                    if (row.groupSettlementItem.groupSettlementId) {
                      errorMsg += ' - ' + row.groupSettlementItem.groupSettlementId
                    }
                    localErrorList.push(errorPrefix + ' ' + errorMsg)
                  }
                })
                this.additionalErrorList.push(...localErrorList)
              }
            }
            this.load = false
            this.logConsole(error)
            this.formatErrorResponse(error)
          })
        }
      },
      groupSettlementCancel () {
        this.groupSettlementStatusChange(this.groupSettlementCancelLink, this.$t('groupSettlement.groupSettlementCanceled'))
      },
      toggleShowTable () {
        if (this.showTable) {
          // this.setMassInput() // is this expensive?
        }
        this.showTable = !this.showTable
      },
      validateBeforeSubmit () {
        this.$v.$touch()
        if (this.$v.massInput.$invalid) {
          EventBus.$emit('alert', {
            variant: 'danger',
            message: this.$t('errors.someErrorsOccuredCheckListBelow'),
            errors: this.getValidationErrors('groupSettlement')
          })
          this.errorList = this.getMassInputErrors(this.massInput)
        } else {
          this.errorList = []
          this.submitGroupSettlement()
        }
      },
      submitGroupSettlement: function (e) {
        this.logConsole('Submitting Group Settlement')
        this.load = true
        this.groupSettlement.groupSettlementItems = []
        this.parseMassInput(this.massInput).forEach(row => {
          if (row === '') {
            return
          }
          let temp = row.match(/\S+/g)
          let invoiceId = temp[0]
          let amount = temp[1] ? Number(temp[1]) : 0
          this.groupSettlement.groupSettlementItems.push({InvoiceId: invoiceId, SettlementAmount: amount})
        })
        this.$http.post(this.editGroupSettlementLink, this.groupSettlement)
        .then(response => {
          this.load = false
          this.logConsole(response)
          EventBus.$emit('alert', {
            variant: 'success',
            message: this.$t('groupSettlement.groupSettlementEdited'),
            errors: []
          })
          this.$nextTick(function () {
            document.body.scrollTop = 0 // safari
            document.documentElement.scrollTop = 0 // chrome, ie, ff
          })
          this.showTable = true
          this.fetchData()
        }, error => {
          this.load = false
          if (typeof error.body.payload !== undefined && error.body.payload !== null) {
            if (typeof error.body.payload.groupSettlementItems !== 'undefined') {
              let localErrorList = []
              Array.from(error.body.payload.groupSettlementItems).forEach(row => {
                if (typeof row.groupSettlementItemError !== 'undefined') {
                  let errorPrefix = this.$t('groupSettlement.massInputError.returnedCodePrefix', [row.groupSettlementItem.invoiceId])
                  let errorMsg = this.$t('apiStatusCodes.' + row.groupSettlementItemError.code)
                  localErrorList.push(errorPrefix + ' ' + errorMsg)
                }
              })
              this.errorList.push(...localErrorList)
            }
          }
          this.logConsole(error)
          this.formatErrorResponse(error)
        })
      },
      reportSettlementIssue () {
        this.load = true
        this.$http.get(Vue.$apiConfig.issueCategoriesGet + '/' + this.IssueCategory[1].DESCRIPTION)
          .then(response => {
            this.logConsole(response)
            this.selectedCategory = null
            this.currentIssueCategory = null
            this.formattedIssueCategories = null
            this.currentIssueSettlementId = null
            this.currentIssueReport = {}
            this.currentIssue = this.IssueCategory[1]
            this.currentIssueCategories = response.data.payload
            this.currentIssueReport.resourceId = this.groupSettlement.groupSettlementId
            this.currentIssueAmount = this.groupSettlement.amount
            this.formatReportCategories(response.data.payload)

            this.$refs.reportIssueModal.show()
            this.load = false
          }, error => {
            this.logConsole(error)
            this.formatErrorResponse(error)
            this.load = false
          })
      },
      categoryChanged () {
        for (let i = 0; i < this.currentIssueCategories.length; i++) {
          if (this.currentIssueCategories[i].id === this.selectedCategory) {
            this.currentIssueCategory = this.currentIssueCategories[i]
            return
          }
        }
      },
      formatReportCategories (categories) {
        this.formattedIssueCategories = []
        for (let i = 0; i < categories.length; i++) {
          if (categories[i].id === 4) {
            if (!this.hasInvoiceSettlements) { continue }
          }
          if (categories[i].id === 5) { continue }
          let category = {
            text: categories[i].categoryCyr,
            value: categories[i].id
          }
          if (this.isCurrentUserDebtor) {
            this.formattedIssueCategories.push(category)
          }
        }
      },
      submitIssueReport () {
        this.currentIssueReport.resource = this.currentIssue.ID

        if (this.currentIssueCategory) {
          this.currentIssueReport.category = this.currentIssueCategory.category
          this.currentIssueReport.requiresData = this.currentIssueCategory.requiresData
          this.currentIssueReport.categoryId = this.currentIssueCategory.id
        }

        if (this.currentIssue.ID === this.IssueCategory[1].ID) {
          if (this.currentIssueCategory && this.currentIssueCategory.id === 3) {
            if (this.currentIssueReport.data && this.currentIssueReport.data) {
              this.currentIssueReport.data = this.currentIssueAmount + ' ' + this.currentIssueReport.data
            }
          } else if (this.currentIssueCategory && this.currentIssueCategory.id === 4) {
            if (this.currentIssueSettlementId && this.currentIssueReport.data && this.currentIssueReport.data) {
              this.currentIssueReport.data = this.currentIssueSettlementId + ' ' + this.currentIssueAmount + ' ' + this.currentIssueReport.data
            }
          }
        }

        let report = []
        report.push(this.currentIssueReport)

        this.load = true
        this.$http.post(Vue.$apiConfig.issueRegisterPost, report)
          .then(response => {
            this.logConsole(response)
            this.load = false

            let reportedIssue = response.data.payload[0]
            EventBus.$emit('alert', {
              variant: 'success',
              message: this.$t('issues.reportIssueSuccess') + ' - ',
              link: '/reported-issues/' + reportedIssue.id,
              linkDescription: reportedIssue.id,
              errors: []
            })
            this.subscribeToIssueProcessedNotifications(reportedIssue.id)
          }, error => {
            this.logConsole(error)
            this.formatErrorResponse(error)
            this.load = false
          })
      },
      subscribeToIssueProcessedNotifications (issueId) {
        this.subscribeToIssueProcessedNotificationType(issueId)
      },
      subscribeToIssueProcessedNotificationType (issueId) {
        this.$http.post(Vue.$config.notification_api_root + '/' + Vue.$apiConfig.notificationSubcribe, {notificationTypes: [this.NotificationTypes.IssueProcessed], resourceId: issueId, resourceType: Vue.$config.issueResourceType})
          .then(response => {
            this.logConsole(response)
            this.subscribeToEmailSink()
          }, error => {
            this.logConsole(error)
          })
      }
    },
    validations: {
      massInput: {
        MassInputValidator: MassInputValidator({minimumInvoices: 2})
      },
      massAdditionInput: {
        MassInputValidator: MassInputValidator({minimumInvoices: 1})
      },
      groupSettlement: {
        groupSettlementNumber: {
          required
        }
      }
    }
  }
</script>
<style lang="scss" type="text/scss" scoped>
  @import '~@/assets/scss/variables.scss';

  @media (max-width: 800px) {
      .budget-info-container {
        position: relative;
        top: 0px;
        right: 0px;
        width: 100% !important;
      }
  }

  .property-label {
    font-size: 11pt;
    font-weight: 400;
  }

  .property-label-caption {
    font-size: 12pt;
    font-weight: 600;
  }

  fieldset label, ul li{
    font-weight: 600;
    font-size: 12pt;
    color: $font-color-dark !important;
  }

  .group-settlement-cancelled-card {
    background-color: transparentize($background-color-danger, .9) !important;
  }

  .group-settlement-active-card {
    background-color: transparentize($background-color-info, .9) !important;
  }

  .group-settlement-cancelled-list, .group-settlement-settled-list {
    background-color: inherit;
  }

  .group-settlement-settled-card {
    background-color: transparentize($background-color-success, .9) !important;
  }

  .text-dark {
    font-weight: 600;
    font-size: 12pt;
    color: $font-color-dark !important;
  }

  .monospace-input {
    font-family:monospace;
  }
  
  .link-text {
    font-weight: 600;
    color: $background-color-accent;
  }

  .invisible-link {
    color: $font-color-dark;
    &:hover {
      text-decoration: none;
    }
  }

  .report-image {
    cursor: pointer;
     width: 36px;
     height: 36px;
  }
</style>
<style lang="scss" type="text/scss">
  .small-column {
    width: 15%;
  }
  
  .tiny-column {
    width: 5%
  }
</style>



// WEBPACK FOOTER //
// SingleGroupSettlement.vue?4664f620